import { Routes } from '@angular/router';
import { Login } from './pages/auth/login/login';
import { ForgotPassword } from './pages/auth/forgot-password/forgot-password';
import { ResetPassword } from './pages/auth/reset-password/reset-password';
import { DashboardLayout } from './layouts/dashboard-layout/dashboard-layout';
import { Dashboard } from './pages/dashboard/dashboard';
import { authGuard } from './core/guards/auth.guard';
import { createPasswordGuard } from './core/guards/create-password.guard';
import { SchedulePage } from './pages/schedule/schedule';
import { Professionals } from './pages/professionals/professionals';
import { PatientsPage } from './pages/patients/patients';
import { PlaceholderPage } from './pages/placeholder/placeholder';
import { Procedures } from './pages/procedures/procedures';
import { RoomsComponent } from './pages/rooms/rooms';
import { Settings } from './pages/settings/settings';
import { LaboratoryComponent } from './pages/laboratory/laboratory';

import { permissionGuard } from './core/guards/permission.guard';
import { planExpiredGuard, planGuard, planLockGuard } from './core/guards/plan.guard';
import { PLAN_SCOPED } from './core/services/clinic-plan.rules';
import { NoPermissionComponent } from './shared/components/no-permission/no-permission.component';
import { opinionsGrants } from './core/auth/opinions-grants';

export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'login', component: Login },
  // Self-serve sign-up is retired (2026-10-20): clinics are created by a MetaSmile
  // super admin in the admin app. Old links land on the login page.
  { path: 'criar-conta', redirectTo: 'login' },
  { path: 'register-success', redirectTo: 'login' },
  { path: 'forgot-password', component: ForgotPassword },
  { path: 'reset-password', component: ResetPassword },
  {
    // Top-level on purpose: outside DashboardLayout, so it is clear of authGuard
    // (which redirects TO here) and of permissionGuard — an account that has just
    // been invited may legitimately have no permissions at all.
    path: 'create-password',
    loadComponent: () =>
      import('./pages/auth/create-password/create-password').then(m => m.CreatePassword),
    canActivate: [createPasswordGuard]
  },

  {
    path: 'formurl',
    loadComponent: () => import('./pages/public-form/public-form').then(m => m.PublicFormComponent)
  },
  {
    path: 'form/:code',
    loadComponent: () => import('./pages/public-form/public-form').then(m => m.PublicFormComponent)
  },
  {
    path: 'feedback/:code',
    loadComponent: () => import('./pages/public-feedback/public-feedback').then(m => m.PublicFeedbackComponent)
  },
  {
    path: 'feedback',
    loadComponent: () => import('./pages/public-feedback/public-feedback').then(m => m.PublicFeedbackComponent)
  },
  {
    path: 'public/registo-paciente/:branchId',
    loadComponent: () => import('./pages/public-patient-registration/public-patient-registration').then(m => m.PublicPatientRegistrationComponent)
  },
  {
    path: 'public/complaints/:code',
    loadComponent: () => import('./pages/public-complaint/public-complaint').then(m => m.PublicComplaintComponent)
  },
  {
    // Without a code there is no route to match, so the address would fall through
    // to the catch-all and land a patient on the staff login. This renders the page's
    // own invalid-address screen instead.
    path: 'public/complaints',
    loadComponent: () => import('./pages/public-complaint/public-complaint').then(m => m.PublicComplaintComponent)
  },
  {
    path: 'public/registo-rapido/:branchId',
    loadComponent: () => import('./pages/public-patient-registration/public-patient-registration').then(m => m.PublicPatientRegistrationComponent),
    data: { mode: 'quick' }
  },

  {
    // Outside DashboardLayout: a branch whose plan has lapsed sees only this.
    path: 'plan-expired',
    loadComponent: () => import('./pages/plan-expired/plan-expired').then(m => m.PlanExpired),
    canActivate: [authGuard, planExpiredGuard],
    data: { [PLAN_SCOPED]: true }
  },
  {
    // Outside DashboardLayout: it fills the Diagnocat popup window and leaves it at once.
    // Outside the layout means outside its planGuard too, so the plan is checked here:
    // a locked branch must not mint a patient's Diagnocat link.
    path: 'diagnocat/open',
    loadComponent: () => import('./pages/diagnocat-open/diagnocat-open').then(m => m.DiagnocatOpen),
    canActivate: [authGuard, planLockGuard, permissionGuard],
    data: { permission: 'Consultas', [PLAN_SCOPED]: true }
  },

  {
    path: '',
    component: DashboardLayout,
    canActivate: [authGuard],
    // The clinic's plan: a lapsed branch is limited to the expired notice (owners keep Settings).
    canActivateChild: [planGuard],
    data: { [PLAN_SCOPED]: true },
    children: [
      { path: 'no-permission', component: NoPermissionComponent, data: { title: 'Acesso Negado' } },
      {
        path: 'dashboard',
        component: Dashboard,
        canActivate: [permissionGuard],
        data: { title: 'Dashboard', description: 'Visão geral da clínica.', permission: 'Dashboard' }
      },
      {
        path: 'schedule',
        loadComponent: () => import('./pages/schedule/schedule.component').then(m => m.ScheduleComponent),
        canActivate: [permissionGuard],
        data: { title: 'Agenda', description: 'Gerencie os agendamentos.', permission: 'Agenda' }
      },
      {
        path: 'schedule/requests',
        loadComponent: () => import('./pages/schedule/components/appointment-requests/appointment-requests.component').then(m => m.AppointmentRequestsComponent),
        canActivate: [permissionGuard],
        data: { title: 'Solicitações de Agendamento', description: 'Pedidos de agendamento dos médicos.', permission: 'Agenda:create' }
      },
      { 
        path: 'professionals', 
        component: Professionals, 
        canActivate: [permissionGuard],
        data: { title: 'Profissionais', description: 'Gerencie a equipe médica.', permission: 'Profissionais' } 
      },
      {
        path: 'professionals/:id',
        loadComponent: () => import('./pages/professionals/pages/doctor-details/doctor-details').then(m => m.DoctorDetailsPage),
        canActivate: [permissionGuard],
        data: { title: 'Detalhes do Profissional', description: 'Visualize as informações do profissional.', permission: 'Profissionais' }
      },
      {
        path: 'opinions',
        loadComponent: () => import('./pages/opinions/opinions').then(m => m.OpinionsPage),
        canActivate: [permissionGuard],
        // The 'Feedback' permission module, not a user type: who reads a patient's rating
        // of a doctor is the clinic's decision (AB#174). The owner passes without a grant,
        // since user_type_id 2 resolves to userStatus 'all'. What the page can actually
        // READ is feedback_tb's policy, which gates the same module - not this guard.
        //
        // ':view' AND NOT THE BARE MODULE NAME, unlike most routes here (precedent:
        // 'Agenda:create' below). get_user_permissions_by_uuid_final emits the bare name
        // for any grant row at all, view ticked or not, while the POLICY tests can_view.
        // Gating on the bare name would admit a profile holding the module with view off
        // to a page the database then empties: 64 visits with every rating blank, which
        // reads as a broken page rather than as a refusal.
        //
        // Holding ANY admits: the page's tabs are separate permission modules, so a reader
        // granted only one still has a tab to read. Feedback:create admits a recorder, who
        // reads existence and their own entries only. Which tabs render is the page's call.
        data: {
          title: 'opinions.title',
          description: 'opinions.description',
          permission: opinionsGrants(),
          // Shown locked to every account when the plan leaves the CRM out (permission guard).
          lockedVisible: true
        }
      },
      { 
        path: 'patients', 
        component: PatientsPage, 
        canActivate: [permissionGuard],
        data: { title: 'Pacientes', description: 'Cadastre e gerencie os pacientes.', permission: 'Pacientes' } 
      },
      {
        path: 'patients/import',
        // Lazy load the new import page
        loadComponent: () => import('./pages/patients/import-patients/import-patients').then(m => m.ImportPatientsPage),
        canActivate: [permissionGuard],
        data: { title: 'Importar Pacientes', description: 'Importação de pacientes via Excel ou CSV.', permission: 'Pacientes' }
      },
      {
        path: 'procedures',
        component: Procedures,
        canActivate: [permissionGuard],
        data: { title: 'Procedimentos', description: 'Catálogo de serviços e tratamentos.', permission: 'Procedimentos' }
      },
      {
        path: 'patients/:branchId/:code',
        loadComponent: () => import('./pages/patients/patient-detail/patient-detail').then(m => m.PatientDetailPage),
        canActivate: [permissionGuard],
        data: { permission: 'Pacientes' }
      },
      {
        // Legacy raw-PK route — kept for any link not yet migrated to branch+code.
        path: 'patients/:id',
        loadComponent: () => import('./pages/patients/patient-detail/patient-detail').then(m => m.PatientDetailPage),
        canActivate: [permissionGuard],
        data: { permission: 'Pacientes' }
      },
      {
        path: 'consultations',
        loadComponent: () => import('./pages/consultations/consultations').then(m => m.ConsultationsComponent),
        canActivate: [permissionGuard],
        data: { title: 'Consultas', description: 'Gerencie os atendimentos clínicos.', permission: 'Consultas' }
      },
      {
        path: 'odontogram',
        loadComponent: () => import('./pages/odontogram/odontogram.component').then(m => m.OdontogramPeriodontogramComponent),
        canActivate: [permissionGuard],
        data: { title: 'Odontograma', permission: 'Pacientes' }
      },
      {
        path: 'financial',
        loadComponent: () => import('./pages/financial/pages/balances-debtors/balances-debtors.component').then(m => m.BalancesDebtorsComponent),
        canActivate: [permissionGuard],
        data: { title: 'Saldos e Devedores', description: 'Gestão de pendências financeiras.', permission: 'Financeiro' }
      },
      {
        path: 'statistics',
        loadComponent: () => import('./pages/statistics/pages/invoicing/invoicing.component').then(m => m.FinancialInvoicingComponent),
        canActivate: [permissionGuard],
        data: { title: 'Faturamento', description: 'Estatísticas de faturação por médico e por sala.', permission: 'Financeiro' }
      },
      {
        path: 'forms',
        loadComponent: () => import('./pages/forms/forms').then(m => m.FormsPage),
        canActivate: [permissionGuard],
        data: { title: 'Formulários', description: 'Modelos de documentos e anamneses.', permission: 'Formulários' }
      },
      {
        path: 'laboratory',
        component: LaboratoryComponent,
        canActivate: [permissionGuard],
        data: { title: 'Laboratórios', description: 'Gestão de laboratórios e pedidos.', permission: 'Laboratórios' }
      },
      {
        path: 'laboratory/:id',
        loadComponent: () => import('./pages/laboratory/components/laboratory-details/laboratory-details').then(m => m.LaboratoryDetailsComponent),
        canActivate: [permissionGuard],
        data: { title: 'Detalhes do Laboratório', description: 'Visualize as informações do laboratório.', permission: 'Laboratórios' }
      },
      {
        path: 'rooms',
        component: RoomsComponent,
        canActivate: [permissionGuard],
        data: { title: 'Salas', description: 'Gestão de ambientes e consultórios.', permission: 'Salas' }
      },
      {
        path: 'settings',
        component: Settings,
        canActivate: [permissionGuard],
        data: { title: 'Configurações', description: 'Ajustes do sistema.', permission: 'Configurações' }
      },
      {
        path: 'audit-logs',
        loadComponent: () => import('./pages/audit-logs/audit-logs').then(m => m.AuditLogsPage),
        canActivate: [permissionGuard],
        data: { title: 'Auditoria', description: 'Histórico de ações realizadas no sistema.', permission: 'Auditoria' }
      },
      {
        // Personal notification feed — no module permission, the parent authGuard
        // is what gates it (same pattern as 'news' below).
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notification-history/notification-history').then(m => m.NotificationHistoryPage),
        data: { title: 'Notificações', description: 'Histórico de notificações e alertas.' }
      },
      {
        path: 'news',
        component: PlaceholderPage,
        data: { title: 'Novidades', description: 'Fique por dentro das atualizações do Meta Smile.' }
      }
    ]
  },
  { path: '**', redirectTo: '/' }
];
